import { err, ok, type Result } from "./result";

declare const nonNegativeIntegerBrand: unique symbol;

export type NonNegativeInteger = number & { readonly [nonNegativeIntegerBrand]: "NonNegativeInteger" };

export type NonNegativeIntegerError =
  | { readonly kind: "invalidInteger" }
  | { readonly kind: "negativeInteger" };

export const validateNonNegativeInteger = (value: number): Result<NonNegativeInteger, NonNegativeIntegerError> => {
  if (!Number.isFinite(value) || !Number.isInteger(value)) return err({ kind: "invalidInteger" });
  return value < 0 ? err({ kind: "negativeInteger" }) : ok(value as NonNegativeInteger);
};

export const validateOptionalNonNegativeInteger = (
  value: number | undefined,
): Result<NonNegativeInteger | undefined, NonNegativeIntegerError> =>
  value === undefined ? ok(undefined) : validateNonNegativeInteger(value);

const DECIMAL_INTEGER_INPUT_RE = /^\d+$/;

export const parseOptionalNonNegativeIntegerInput = (
  value: string,
): Result<NonNegativeInteger | undefined, NonNegativeIntegerError> => {
  const cleaned = value.trim();
  if (cleaned === "") return ok(undefined);
  if (cleaned.startsWith("-")) return err({ kind: "negativeInteger" });
  if (!DECIMAL_INTEGER_INPUT_RE.test(cleaned)) return err({ kind: "invalidInteger" });
  return validateNonNegativeInteger(Number(cleaned));
};

export type BoundedIntegerBounds = {
  readonly defaultValue: number;
  readonly min: number;
  readonly max: number;
};

export const normalizeBoundedInteger = (
  value: number | null | undefined,
  bounds: BoundedIntegerBounds,
): number => {
  const candidate = value == null || !Number.isFinite(value) ? bounds.defaultValue : value;
  return Math.max(bounds.min, Math.min(Math.floor(candidate), bounds.max));
};

declare const takeLimitBrand: unique symbol;

export type TakeLimit = number & { readonly [takeLimitBrand]: "TakeLimit" };

export type TakeLimitBounds = BoundedIntegerBounds;

export const normalizeTakeLimit = (
  value: number | null | undefined,
  bounds: TakeLimitBounds,
): TakeLimit => normalizeBoundedInteger(value, bounds) as TakeLimit;

export const formatByteSize = (bytes: number): string => {
  const value = Number.isFinite(bytes) ? Math.max(0, Math.floor(bytes)) : 0;
  if (value < 1024) return value + " B";
  if (value < 1024 * 1024) return (value / 1024).toFixed(1) + " KB";
  return (value / 1024 / 1024).toFixed(1) + " MB";
};
